import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { ImageService } from '../../../../services/image/image.service';
import { BriefProgressEnum } from '../../../../common/general-types/briefs.dto';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { MbaIcon } from '../assignment/atoms/mba-icon/mba-icon';
import { ui } from '../assignment/assignment-ui';
import { ApprovedContentService } from './approved-content.service';
import { ApprovedVideo, FolderLayout } from './approved-content.types';
import {
  CheckState,
  DeliverableFilter,
  SortKey,
  StatusFilter,
  checkState,
  deliverableCounts,
  deliverableName,
  deliverableSub,
  downloadRoot,
  firstName,
  shortCreatorName,
  formatApprovedDate,
  formatBytes,
  formatDuration,
  resolveCurrent,
  statusCounts,
  stepCurrent,
  toggleIds,
  totalBytes,
  visibleVideos,
} from './approved-content.helpers';
import { ApprovedPlayer } from './molecules/approved-player/approved-player';
import { DownloadModal } from './molecules/download-modal/download-modal';

/** Which videos the open download modal is for. */
type ModalTarget = { kind: 'all' } | { kind: 'selection' } | { kind: 'one'; id: string };

interface SavedPrefs {
  status: StatusFilter;
  deliverable: DeliverableFilter;
  sort: SortKey;
  includeSheet: boolean;
  layout: FolderLayout;
}

const PREFS_KEY = 'mbt-approved-content-v1';
const DEFAULT_PREFS: SavedPrefs = { status: 'all', deliverable: 'all', sort: 'approved', includeSheet: false, layout: 'creator' };

function readPrefs(): SavedPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...(JSON.parse(raw) as Partial<SavedPrefs>) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

/**
 * Manage Brief → Approved Content: every approved video on the brief, with a preview and
 * downloads of one, a selection, or all of them (MP4, or a ZIP with an optional content sheet).
 */
@Component({
  selector: 'mbt-approved-content',
  imports: [MbaIcon, ApprovedPlayer, DownloadModal],
  templateUrl: './approved-content.html',
  styleUrl: './approved-content.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block h-full min-h-0',
    '(document:keydown)': 'onKeydown($event)',
  },
})
export class ApprovedContent {
  private readonly tabs = inject(ManageBriefTabsService);
  private readonly approvedContent = inject(ApprovedContentService);
  private readonly toast = inject(ToastNotificationService);
  private readonly imageService = inject(ImageService);

  protected readonly ui = ui;
  protected readonly deliverableName = deliverableName;
  protected readonly deliverableSub = deliverableSub;
  protected readonly shortCreatorName = shortCreatorName;
  protected readonly formatBytes = formatBytes;
  protected readonly formatDuration = formatDuration;
  protected readonly formatApprovedDate = formatApprovedDate;

  private readonly player = viewChild(ApprovedPlayer);

  // ── data ───────────────────────────────────────────────────────────────
  protected readonly briefId = computed(() => this.tabs.focussedBrief()?.id ?? null);
  protected readonly state = this.approvedContent.state;
  protected readonly data = computed(() => {
    const s = this.state();
    return s.status === 'loaded' && s.briefId === this.briefId() ? s.data : null;
  });
  protected readonly loading = computed(() => this.state().status === 'loading' || this.state().status === 'idle');
  protected readonly failed = computed(() => this.state().status === 'error');
  protected readonly videos = computed<readonly ApprovedVideo[]>(() => this.data()?.videos ?? []);
  protected readonly hiredCount = computed(() => this.tabs.filterStats()['Hired'] ?? 0);

  // ── UI state ───────────────────────────────────────────────────────────
  private readonly prefs = readPrefs();
  protected readonly status = signal<StatusFilter>(this.prefs.status);
  protected readonly deliverable = signal<DeliverableFilter>(this.prefs.deliverable);
  protected readonly sort = signal<SortKey>(this.prefs.sort);
  protected readonly includeSheet = signal(this.prefs.includeSheet);
  protected readonly layout = signal<FolderLayout>(this.prefs.layout);
  protected readonly query = signal('');
  protected readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  private readonly currentIdRaw = signal<string | null>(null);
  protected readonly modal = signal<ModalTarget | null>(null);
  protected readonly downloading = signal(false);

  // ── derived ────────────────────────────────────────────────────────────
  protected readonly visible = computed(() =>
    visibleVideos(this.videos(), {
      status: this.status(),
      deliverable: this.deliverable(),
      query: this.query(),
      sort: this.sort(),
    }),
  );
  protected readonly currentId = computed(() => resolveCurrent(this.visible(), this.currentIdRaw()));
  protected readonly current = computed(() => this.visible().find((v) => v.id === this.currentId()) ?? null);
  protected readonly currentPosition = computed(() => this.visible().findIndex((v) => v.id === this.currentId()));

  protected readonly summary = computed(() => {
    const videos = this.videos();
    return {
      count: videos.length,
      creators: new Set(videos.map((v) => v.creator.id)).size,
      size: formatBytes(totalBytes(videos)),
      notDownloaded: videos.filter((v) => !v.downloadedByMe).length,
    };
  });
  protected readonly statusCounts = computed(() => statusCounts(this.videos(), this.deliverable(), this.query()));
  protected readonly deliverableOptions = computed(() => {
    const options = this.data()?.deliverables ?? [];
    const counts = deliverableCounts(this.videos(), this.status(), this.query(), options.map((o) => o.index));
    return {
      all: counts.all,
      items: options.map((o) => ({ index: o.index, label: deliverableName(o.index), count: counts.byIndex.get(o.index) ?? 0 })),
    };
  });
  protected readonly headerCheck = computed<CheckState>(() =>
    checkState(this.selectedIds(), this.visible().map((v) => v.id)),
  );
  protected readonly selectedVideos = computed(() => this.videos().filter((v) => this.selectedIds().has(v.id)));
  protected readonly selectedSize = computed(() => formatBytes(totalBytes(this.selectedVideos())));

  protected readonly modalVideos = computed<readonly ApprovedVideo[]>(() => {
    const target = this.modal();
    if (!target) return [];
    if (target.kind === 'all') return this.videos();
    if (target.kind === 'selection') return this.selectedVideos();
    return this.videos().filter((v) => v.id === target.id);
  });
  protected readonly modalTitle = computed(() => {
    const target = this.modal();
    const videos = this.modalVideos();
    if (videos.length === 1) {
      const v = videos[0];
      return `Download ${firstName(v.creator.name)}’s ${deliverableName(v.deliverableIndex)}`;
    }
    return target?.kind === 'all' ? `Download All ${videos.length} Videos` : `Download ${videos.length} Videos`;
  });
  protected readonly downloadRoot = computed(() => downloadRoot(this.data()?.brandName ?? '', this.data()?.briefTitle ?? ''));

  constructor() {
    // Only the brief id is a dependency: load() reads the service state, and tracking that
    // would re-run this after every response (and loop on errors).
    effect(() => {
      const id = this.briefId();
      if (id) untracked(() => this.approvedContent.load(id));
    });
    // A new brief starts with nothing selected.
    effect(() => {
      this.briefId();
      this.selectedIds.set(new Set());
      this.currentIdRaw.set(null);
    });
    effect(() => {
      const prefs: SavedPrefs = {
        status: this.status(),
        deliverable: this.deliverable(),
        sort: this.sort(),
        includeSheet: this.includeSheet(),
        layout: this.layout(),
      };
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
      } catch {
        // storage unavailable (private mode): keep the in-memory choice
      }
    });
  }

  // ── actions ────────────────────────────────────────────────────────────
  protected retry(): void {
    const id = this.briefId();
    if (id) this.approvedContent.load(id, true);
  }

  protected setQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected clearFilters(): void {
    this.status.set('all');
    this.deliverable.set('all');
    this.query.set('');
  }

  protected preview(id: string): void {
    if (id === this.currentId()) return;
    this.player()?.pause();
    this.currentIdRaw.set(id);
  }

  protected step(delta: 1 | -1): void {
    const next = stepCurrent(this.visible(), this.currentId(), delta);
    if (next) this.preview(next);
  }

  protected toggleOne(id: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedIds.set(toggleIds(this.selectedIds(), [id]));
  }

  protected toggleVisible(): void {
    this.selectedIds.set(toggleIds(this.selectedIds(), this.visible().map((v) => v.id)));
  }

  protected clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  protected openDownload(target: ModalTarget, event?: Event): void {
    event?.stopPropagation();
    // A single video starts plain (just the MP4); all / selection keep the last choice.
    if (target.kind === 'one') this.includeSheet.set(false);
    this.modal.set(target);
  }

  protected closeModal(): void {
    if (!this.downloading()) this.modal.set(null);
  }

  protected confirmDownload(): void {
    const briefId = this.briefId();
    const videos = this.modalVideos();
    if (!briefId || videos.length === 0 || this.downloading()) return;
    this.downloading.set(true);
    this.approvedContent
      .download(briefId, { videoIds: videos.map((v) => v.id), includeSheet: this.includeSheet(), layout: this.layout() })
      .subscribe({
        next: (result) => {
          this.downloading.set(false);
          this.modal.set(null);
          this.selectedIds.set(new Set());
          this.approvedContent.markDownloaded(briefId, result.videoIds);
          startBrowserDownload(result.url, result.fileName);
          void this.toast.alert({
            title: result.kind === 'zip' ? `Downloading ZIP of ${result.videoIds.length} video${result.videoIds.length === 1 ? '' : 's'}` : `Downloading ${result.fileName}`,
            message: result.kind === 'zip' ? result.fileName : 'Check your downloads folder.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: unknown) => {
          this.downloading.set(false);
          const tooLarge = err instanceof HttpErrorResponse && err.status === 413;
          void this.toast.alert({
            title: tooLarge ? 'That download is too large' : 'Download failed',
            message: tooLarge ? 'Choose fewer videos and download them in batches.' : 'Please try again in a moment.',
            type: 'error',
            icon: 'error',
          });
        },
      });
  }

  protected reviewVideos(): void {
    this.tabs.navigateToBriefTab(BriefProgressEnum.ActivityFeed);
  }

  /** ↑ / ↓ move the preview; Space selects the previewed video. Not while typing or in the modal. */
  protected onKeydown(event: KeyboardEvent): void {
    if (this.modal() || !this.videos().length) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const next = stepCurrent(this.visible(), this.currentId(), event.key === 'ArrowDown' ? 1 : -1);
      if (next) {
        event.preventDefault();
        this.preview(next);
      }
    } else if (event.key === ' ' && target === document.body && this.currentId()) {
      event.preventDefault();
      this.toggleOne(this.currentId()!);
    }
  }

  // ── classes ────────────────────────────────────────────────────────────
  protected segClass(on: boolean): string {
    return `inline-flex cursor-pointer items-center gap-1.5 rounded-full px-[13px] py-1.5 text-[13px] font-semibold whitespace-nowrap transition-colors duration-[140ms] ${ui.focusRing} ${
      on ? 'bg-(--mba-surface) text-(--fg-1) shadow-[0_1px_2px_rgba(18,6,43,0.06)]' : 'text-(--fg-2) hover:text-(--fg-1)'
    }`;
  }

  protected segCountClass(on: boolean): string {
    return `text-[11px] font-bold tabular-nums ${on ? 'text-(--app-accent-strong)' : 'text-(--fg-3)'}`;
  }

  protected sortClass(key: SortKey): string {
    return `inline-flex cursor-pointer items-center gap-1 uppercase tracking-[0.06em] hover:text-(--fg-1) ${ui.focusRing} ${
      this.sort() === key ? 'text-(--app-accent-strong)' : ''
    }`;
  }

  protected checkboxClass(state: CheckState): string {
    return `grid size-5 shrink-0 cursor-pointer place-items-center rounded-[6px] border-[1.5px] text-white transition-colors duration-[140ms] hover:border-(--app-accent) ${ui.focusRing} ${
      state === 'off' ? 'border-(--border-strong) bg-(--mba-surface)' : 'border-(--app-accent) bg-(--app-accent)'
    }`;
  }

  protected rowClass(video: ApprovedVideo): string {
    return [
      'cursor-pointer transition-colors duration-[140ms] hover:bg-(--mba-row-hi)',
      this.selectedIds().has(video.id) ? 'bg-(--app-accent-soft)' : '',
      this.currentId() === video.id ? 'shadow-[inset_3px_0_0_var(--app-accent)]' : '',
    ].join(' ');
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  /** Resized profile photo through the image service (stored values are S3 keys). */
  protected creatorPhoto(video: ApprovedVideo): string | null {
    return video.creator.photoURL ? this.imageService.getImageSource(video.creator.photoURL, 'gallery') : null;
  }

  protected creatorInitial(name: string): string {
    return (name.trim()[0] ?? '?').toUpperCase();
  }

  /** Width ÷ height for the player (videoFormat 1 = landscape, 3 = image, else portrait). */
  protected aspectRatio(video: ApprovedVideo): number {
    return video.videoFormat === 1 ? 16 / 9 : video.videoFormat === 3 ? 1 : 9 / 16;
  }

  protected formatLabel(video: ApprovedVideo): string {
    return video.videoFormat === 1 ? 'Landscape' : video.videoFormat === 3 ? 'Image' : 'Portrait';
  }
}

/** Opens a signed URL that answers with Content-Disposition: attachment, so the page stays put. */
function startBrowserDownload(url: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
